import { html, KompisElement, property } from '@inter-ikea-kompis/web-component';
import type { ILocalizedInformation, ITheme } from '@inter-ikea-kompis/types';
import type { RenderResult } from '@inter-ikea-kompis/web-component';

import type { IMediaItem } from '@inter-ikea-kompis/component-media-carousel';
import { MediaCarousel } from '@inter-ikea-kompis/component-media-carousel';

import type ISummaryPageTranslations from '../../types/ISummaryPageTranslations.js';

/**
 * The view for the Summary Page Images.
 *
 * @event change Triggered when the user changes to a new image
 */

export default class SummaryPageImages extends KompisElement {
	// Theme.
	@property({ type: Object, required: true })
	public theme!: ITheme;

	// Localized information. Deprecated - use "translations" instead.
	@property({ type: Object })
	public localizedInformation: ILocalizedInformation | null = null;

	// The translations retrieved from translations service.
	@property({ type: Object })
	public translations: ISummaryPageTranslations | null = null;

	// List of media items
	@property({ type: Object })
	public images: IMediaItem[] | null = null;

	// Index of the current main image
	private selectedImageIndex = 0;

	public override render(): RenderResult {
		// We want translations to be required, but since both props to provide translations are optional, this check is needed until localizedInformation is removed.
		if (!this.hasTranslations()) {
			// We can't throw an error because on the first render no props are set.
			return null;
		}
		return html` ${this.getMediaCarousel()} `;
	}

	public override connectedCallback(): void {
		this.customElements.define('kompis-media-carousel', MediaCarousel);

		super.connectedCallback();
	}

	private hasTranslations(): boolean {
		return !!this.translations || !!this.localizedInformation;
	}
	/**
	 * Get Media Carousel. When images prop is undefined, the carousel is not shown.
	 *
	 * @returns The store selector.
	 */
	private getMediaCarousel(): RenderResult {
		if (!this.images) {
			return null;
		}

		// Fallback to first image if selected image is out of range.
		if (this.selectedImageIndex >= this.images.length) {
			this.selectedImageIndex = 0;
		}

		return html` <kompis-media-carousel
			.localizedInformation="${this.localizedInformation}"
			.translations="${this.translations}"
			.theme="${this.theme}"
			.selectedIndex="${this.selectedImageIndex}"
			.mediaItems="${this.images}"
			.showScrollbar="${true}"
			.showArrows="${true}"
			@change="${({ detail }): void => {
				this.selectedImageIndex = detail.selectedIndex;
				this.dispatchEvent(
					new CustomEvent('change', {
						bubbles: true,
						detail: { selectedIndex: detail.selectedIndex }
					})
				);
				this.requestUpdate();
			}}"
		></kompis-media-carousel>`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'kompis-summary-page-images': SummaryPageImages;
	}
}
